// proof-of-identity.ts (TypeScript)
import { Modal } from "bootstrap";
import DocumentState from "./document-state";
import {
    CategoryCombination,
    CategoryType,
    DocumentType,
    IdentityDocument,
    IdentityDocumentsSubmission
} from "./identity-models";

/**
 * Main refactored class that dynamically builds
 * the proof-of-identity UI from 3 data sources:
 *  - combinations
 *  - category types
 *  - documents
 */
export default class ProofOfIdentityModal {
    private modalElement: HTMLDivElement;

    private initialQuestionSection: HTMLDivElement | null;
    private combinationSection: HTMLDivElement | null;
    private combinationCtaList: HTMLDivElement | null;

    private documentListSection: HTMLDivElement | null;
    private documentListItems: HTMLDivElement | null;
    private documentListButton: HTMLDivElement | null;

    private identityDocumentSelectionSection: HTMLDivElement | null;
    private identityDocumentsListsContainer: HTMLDivElement | null;

    private finalCheckSection: HTMLDivElement | null;
    private signatureYes: HTMLInputElement | null;
    private signatureNo: HTMLInputElement | null;
    private nameYes: HTMLInputElement | null;
    private nameNo: HTMLInputElement | null;
    private signatureWarning: HTMLDivElement | null;
    private nameChangeWarning: HTMLDivElement | null;
    private nameChangeDropdownContainer: HTMLDivElement | null;

    private checklistReviewSection: HTMLDivElement | null;
    private contactDetailsSection: HTMLDivElement | null;
    private checklistSentSection: HTMLDivElement | null;

    private closeButton: HTMLButtonElement | null;
    private backButton: HTMLButtonElement | null;


    // Email fields
    private emailInput: HTMLInputElement | null;
    private emailErrorMessage: HTMLDivElement | null;
    private emailSendButton: HTMLButtonElement | null;
    private checklistCloseButton: HTMLButtonElement | null;

    // API data
    private combinations: CategoryCombination[] = [];
    private categoryTypes: CategoryType[] = [];
    private primaryIdCategory: CategoryType;
    private secondaryIdCategory: CategoryType;
    private nameChangeCategory: CategoryType;
    private documents: DocumentType[] = [];

    // Current user selections
    private documentState: DocumentState;
    private currentCombinationId: number | null = null;
    private previousStep: string | null = null;
    private currentStep: string = "initialquestion";

    private activeCategoryId: number;
    private totalDocumentListItems: number = 0;

    // Regex
    private emailRegex = /^[\w\.\-\+]+@([\w\-]+\.)+[a-zA-Z]{2,}$/;

    constructor(modalID: string) {
        this.modalElement = document.getElementById(modalID) as HTMLDivElement;

        // Grab partial-based sections by class
        this.initialQuestionSection = this.modalElement.querySelector(".initial-question-section");;
        this.combinationSection = this.modalElement.querySelector(".combination-section");
        this.combinationCtaList = this.modalElement.querySelector(".combination-cta-list");

        this.documentListSection = this.modalElement.querySelector(".document-list-section");
        this.documentListItems = this.documentListSection.querySelector(".document-list-items");
        this.documentListButton = this.documentListSection.querySelector(".document-list-button .button");

        this.identityDocumentSelectionSection = this.modalElement.querySelector(".identity-document-selection-section");
        this.identityDocumentsListsContainer = this.identityDocumentSelectionSection.querySelector(".identity-documents-list-container");

        this.finalCheckSection = this.modalElement.querySelector(".final-check-section");
        this.signatureYes = this.modalElement.querySelector("#signatureYes") as HTMLInputElement;
        this.signatureNo = this.modalElement.querySelector("#signatureNo") as HTMLInputElement;
        this.nameYes = this.modalElement.querySelector("#nameYes") as HTMLInputElement;
        this.nameNo = this.modalElement.querySelector("#nameNo") as HTMLInputElement;
        this.signatureWarning = this.modalElement.querySelector("#signatureWarning") as HTMLDivElement;
        this.nameChangeWarning = this.modalElement.querySelector("#nameChangeWarning") as HTMLDivElement;
        this.nameChangeDropdownContainer = this.modalElement.querySelector("#nameChangeDropdown") as HTMLDivElement;

        this.checklistReviewSection = this.modalElement.querySelector(".checklist-review-section");
        this.contactDetailsSection = this.modalElement.querySelector(".contact-details-section");
        this.checklistSentSection = this.modalElement.querySelector(".checklist-sent-section");

        // Buttons
        this.closeButton = this.modalElement.querySelector(".close-modal");
        this.backButton = this.modalElement.querySelector(".back-button");

        this.emailInput = this.modalElement.querySelector(".contact-email .email-input");
        this.emailErrorMessage = this.modalElement.querySelector(".contact-email .error-message");
        this.emailSendButton = this.modalElement.querySelector(".contact-details-button .button");
        this.checklistCloseButton = this.modalElement.querySelector(".checklist-close-button .button");

        this.documentState = new DocumentState();

        this.init();
    }

    private async init() {
        await this.fetchData();     // Get all combination/category/docs from API
        //this.buildCombinationUI();  // Build the combination buttons from data
        this.addGlobalEventListeners();
        this.attachInitialQuestionListeners()
    }

    private addGlobalEventListeners() {
        // Close
        this.closeButton?.addEventListener("click", () => this.resetModal());
        this.modalElement.addEventListener("hide.bs.modal", () => this.resetModal());

        // Back
        this.backButton?.addEventListener("click", () => this.onBackButtonClick());

        // Confirm selection => contact
        this.documentListButton?.addEventListener("click", this.onConfirmSelectionClick.bind(this));

        // Email
        this.emailInput?.addEventListener("blur", () => this.validateEmail());
        this.emailSendButton?.addEventListener("click", () => this.sendIdentityDocuments());

        // Checklist close
        this.checklistCloseButton?.addEventListener("click", () => {
            const modalInstance = Modal.getInstance(this.modalElement);
            modalInstance?.hide();
            this.resetModal();
        });

        const nameChangeContainer = this.modalElement.querySelector("#nameChangeDropdown") as HTMLDivElement;
        const nameChangeSelect = nameChangeContainer.querySelector(".document-select-dropdown") as HTMLDivElement;
        if (nameChangeSelect) {
            this.nameChangeCategory = this.categoryTypes.find(cat => cat.name.toLowerCase() === "name change");
            if (this.nameChangeCategory) {
                nameChangeSelect.addEventListener("click", (e) => {
                    this.activeCategoryId = this.nameChangeCategory.itemID;

                    e.preventDefault();
                    this.buildGlobalIdentityList(this.nameChangeCategory.itemID, nameChangeContainer);
                    this.showStep("identity");
                });

            }
        }

    }

    private async fetchData() {
        try {
            const [combResp, catResp, docResp] = await Promise.all([
                fetch("/api/proofofidentity/combinations"),
                fetch("/api/proofofidentity/categories"),
                fetch("/api/proofofidentity/documents"),
            ]);
            if (!combResp.ok || !catResp.ok || !docResp.ok) {
                throw new Error("Failed to fetch data");
            }
            this.combinations = await combResp.json();
            this.categoryTypes = await catResp.json();
            this.documents = await docResp.json();
        } catch (err) {
            console.error("Error fetching PoI data:", err);
        }
    }

    private attachInitialQuestionListeners() {
        // Select "Yes" and "No" buttons
        const yesButton = this.modalElement.querySelector(".initial-question-yes-button .button") as HTMLButtonElement;
        const noButton = this.modalElement.querySelector(".initial-question-no-button .button") as HTMLButtonElement;

        if (!yesButton || !noButton) return;

        // If "Yes" is clicked, go to the Combination Checklist
        yesButton.addEventListener("click", () => {
            this.renderExistingLicencePath();
        });

        // If "No" is clicked, go to the new path (to be implemented)
        noButton.addEventListener("click", () => {
            this.showStep("combination");
            this.buildCombinationUI()
        });
    }

    /**
     *  Method to render the document selection view but with only the Primary ID category and the Secondary ID category
     */
    private renderExistingLicencePath() {

        if (!this.documentListItems) return;
        this.documentListItems.innerHTML = ""; // Clear previous selections

        // Fetch categories for Primary and Secondary ID
        this.primaryIdCategory = this.categoryTypes.find(cat => cat.name.toLowerCase() === "primary id");
        this.secondaryIdCategory = this.categoryTypes.find(cat => cat.name.toLowerCase() === "secondary id");
        const categories: CategoryType[] = [this.primaryIdCategory, this.secondaryIdCategory];
        if (!this.primaryIdCategory || !this.secondaryIdCategory) {
            console.error("Primary or Secondary ID categories not found.");
            return;
        }

        const listContainer = document.createElement("list");
        listContainer.classList.add("document-list-item");
        listContainer.setAttribute("data-combination-id", "");

        const heading = document.createElement("h3");
        heading.classList.add("h4");
        heading.textContent = "Identification Documents";
        listContainer.appendChild(heading);

        const subheading = document.createElement("h4");
        subheading.classList.add("document-list-count");
        this.totalDocumentListItems = 2;
        subheading.textContent = `0/${this.totalDocumentListItems} documents selected`;
        listContainer.appendChild(subheading);

        // Create document select for each category
        categories.forEach(category => {
            const container = this.createDocumentSelectContainer(category);
            listContainer.appendChild(container)
        })
        this.documentListItems.appendChild(listContainer);

        this.showStep("documents");
    }

    /** Build the combination CTA buttons inside .combination-cta-list */
    private buildCombinationUI() {
        if (!this.combinationCtaList) return;

        // Clear any existing
        this.combinationCtaList.innerHTML = "";

        // Get our hidden template
        const templateEl = document.getElementById("combinationButtonTemplate") as HTMLDivElement;
        if (!templateEl) {
            console.warn("No combinationButtonTemplate found in DOM");
            return;
        }

        // We'll use 'templateEl.firstElementChild' to clone the actual <button> block
        const templateButton = templateEl.querySelector(".cta-button");
        if (!templateButton) {
            console.warn("No .cta-button found inside combinationButtonTemplate");
            return;
        }

        // For each combination item we got from the API
        this.combinations.forEach((combo) => {
            // Clone the entire button block
            const comboButton = templateButton.cloneNode(true) as HTMLButtonElement;

            // Set data-id to your combo.itemID
            comboButton.setAttribute("data-id", combo.itemID.toString());

            //    Replace button combo text 
            const buttonContentSpan = comboButton.querySelector(".button-content") as HTMLSpanElement;
            if (buttonContentSpan) {
                buttonContentSpan.textContent = combo.displayName.toUpperCase() || combo.name.toUpperCase();
            }

            // Add categories
            const buttonDiv = comboButton.querySelector(".button .combo") as HTMLUListElement;
            if (buttonDiv) {
                // Clear any placeholder <li>
                const catsUl = document.createElement("div");
                catsUl.classList.add("combination-documents");

                catsUl.innerHTML = "";

                const catIds = combo.categoryType ? combo.categoryType.split(";") : [];
                const catQty = combo.categoryQuantity ? combo.categoryQuantity.split(",") : [];

                catIds.forEach((catIdStr, index) => {
                    const qty = parseInt(catQty[index] || "1", 10);
                    const catId = parseInt(catIdStr, 10);
                    const catObj = this.categoryTypes.find(ct => ct.itemID === catId);
                    const catName = catObj ? catObj.name : "Unknown Category";
                    const li = document.createElement("div");
                    li.textContent = `${qty} ${catName} document${qty > 1 ? "s" : ""}`;
                    catsUl.appendChild(li);
                });

                buttonDiv.appendChild(catsUl)
            }

            comboButton.addEventListener("click", (e) => {
                e.preventDefault();
                this.currentCombinationId = combo.itemID;
                this.buildDocumentListUI(this.currentCombinationId);
                this.showStep("documents");
            });

            // 6. Append the newly cloned button to .combination-cta-list
            this.combinationCtaList.appendChild(comboButton);
        });
    }

    /**
     * Dynamically create the .document-list-item block for the chosen combination
     */
    private buildDocumentListUI(combinationId: number) {
        if (!this.documentListItems) return;
        this.documentListItems.innerHTML = "";

        // Create a single item for that combination
        const docItemDiv = document.createElement("div");
        docItemDiv.classList.add("document-list-item", `list-${combinationId}`);
        docItemDiv.setAttribute("data-combination-id", combinationId.toString());

        const comboObj = this.combinations.find(c => c.itemID === combinationId);
        const heading = document.createElement("h3");
        heading.classList.add("h4");
        heading.textContent = comboObj?.displayName ?? comboObj?.name ?? "Documents";
        docItemDiv.appendChild(heading);

        // Create a subheading for tracking document selection
        const subheading = document.createElement("h4");
        subheading.classList.add("document-list-count");
        subheading.textContent = `0/0 documents selected`; // Default text before population
        docItemDiv.appendChild(subheading);

        // e.g. comboObj.categoryType === "1,3,5"
        //      comboObj.categoryQuantity === "2,3,1"
        if (comboObj && comboObj.categoryType && comboObj.categoryQuantity) {
            const categoryIds = comboObj.categoryType.split(";");        // ["1","3","5"]
            const categoryQty = comboObj.categoryQuantity.split(",");    // ["2","3","1"]

            // Loop through each category in the split array
            categoryIds.forEach((catIdStr, index) => {
                const catIdNum = parseInt(catIdStr, 10);
                const qty = parseInt(categoryQty[index], 10) || 1;
                this.totalDocumentListItems += qty;
                // Find the actual Category object from your 'categoryTypes' array
                const catObject = this.categoryTypes.find(ct => ct.itemID === catIdNum);
                if (catObject) {
                    // Create 'qty' number of document-select containers for this category
                    for (let i = 0; i < qty; i++) {
                        const container = this.createDocumentSelectContainer(catObject);
                        docItemDiv.appendChild(container);
                    }
                }
            });
        }
        this.documentListItems.appendChild(docItemDiv);
        subheading.textContent = `0/${this.totalDocumentListItems} documents selected`;
    }


    /**
     * Create a .document-select-container block for a single category
     */
    private createDocumentSelectContainer(cat: CategoryType): HTMLDivElement {
        const wrapper = document.createElement("div");
        wrapper.classList.add("document-select-container");

        // Create a unique ID for this container
        const docSelectId = crypto.randomUUID(); // or any unique method
        wrapper.setAttribute("data-documentselect-id", docSelectId);
        wrapper.setAttribute("data-category-id", cat.itemID.toString());

        // Label
        const label = document.createElement("label");
        label.classList.add("document-select-category");
        label.innerHTML = `
      ${cat.name}
      <span class="document-selected-icon"><i class="fa-solid fa-circle-check" style="color:green"></i></span>
      <span class="document-error-icon"><i class="fa-solid fa-exclamation-circle" style="color:red"></i></span>
    `;
        wrapper.appendChild(label);

        // Dropdown
        const dropdownDiv = document.createElement("div");
        dropdownDiv.classList.add("document-select-dropdown");
        dropdownDiv.innerHTML = `
      <div class="document-name">
          <div class="document-name-text">Select a document</div>
          <i class="dot-icon dot-stroke-forward-arrow"></i>
      </div>
      <a href="#" class="change-document-link">Change</a>
    `;
        wrapper.appendChild(dropdownDiv);

        // Error
        const errorDiv = document.createElement("div");
        errorDiv.classList.add("document-select-error");
        errorDiv.textContent = "Please select a document";
        wrapper.appendChild(errorDiv);

        // Listen for the dropdown click => toggle identity list
        dropdownDiv.addEventListener("click", (e) => {
            e.preventDefault();
            this.activeCategoryId = cat.itemID;

            this.showStep("identity");
            this.buildGlobalIdentityList(this.activeCategoryId, wrapper);
        });

        return wrapper;
    }

    /**
     * Toggle the "identity types list" for a category
     * (the .identity-documents-list inside identity-document-selection-section)
     */
    private buildGlobalIdentityList(categoryId: number, container: HTMLDivElement) {
        if (!this.identityDocumentsListsContainer) return;
        this.identityDocumentsListsContainer.innerHTML = "";

        // Create a single container with your styling classes
        const listDiv = document.createElement("div");
        listDiv.classList.add("identity-documents-list", `identityDocumentssList-${categoryId}`);

        // Optional heading
        const catObj = this.categoryTypes.find((c) => c.itemID === categoryId);
        const heading = this.identityDocumentSelectionSection.querySelector("h3");
        if (heading) {
            heading.textContent = `Select a ${catObj?.name} document`;
        }

        // if warning label render warning label
        const warningDiv: HTMLDivElement = this.identityDocumentSelectionSection.querySelector(".identity-document-warning ");
        if (warningDiv) {
            if (!catObj.warningLabel || catObj.warningLabel.trim() === "") {
                warningDiv.style.display = "none";
            } else {
                const alertText = warningDiv.querySelector(".alert-text");
                alertText.innerHTML = catObj.warningLabel;
                warningDiv.style.display = "block";
            }

        }

        // Create the <ul> for doc items
        const ul = document.createElement("ul");
        listDiv.appendChild(ul);

        // Filter the doc items for this category
        const relevantDocs = this.documents.filter(d =>
            d.categoryType.includes(categoryId.toString())
        );

        // Loop through relevant docs and create a div
        relevantDocs.forEach(doc => {
            const li = document.createElement("li");
            li.classList.add("identity-documents-list-items");

            const containerTextDiv = document.createElement("div");

            // Document name
            const nameDiv = document.createElement("div");
            nameDiv.classList.add("identity-document-name");
            nameDiv.textContent = doc.name;
            containerTextDiv.appendChild(nameDiv);



            // Tooltip description
            if ((doc.tooltipText ?? "").trim() !== "") {
                const infoTextDiv = document.createElement("div");
                infoTextDiv.classList.add("info-text");
                infoTextDiv.innerHTML = `View important information <i class="dot-icon dot-stroke-alert-info"></i>`;

                // Attach tooltip functionality
                infoTextDiv.addEventListener("mouseenter", (event) => {
                    this.showTooltip(event, doc.tooltipText);
                });

                infoTextDiv.addEventListener("mouseleave", this.hideTooltip);

                containerTextDiv.appendChild(infoTextDiv);
            }
            li.appendChild(containerTextDiv);

            const containerImageDiv = document.createElement("div");
            containerImageDiv.classList.add("select-button-container");

            // Preview image
            const imgDiv = document.createElement("img");
            imgDiv.classList.add("identity-preview-image");
            imgDiv.src = doc.previewImage;
            containerImageDiv.appendChild(imgDiv);

            // "Select" button
            const btn = document.createElement("button");
            btn.classList.add("select-button");
            btn.type = "button";
            btn.textContent = "Select";
            btn.addEventListener("click", () => {
                // Use the container we saved in a field, e.g. this.activeContainer
                this.onSelectDocument(categoryId, doc.itemID, doc.name, container);
                this.showStep(this.previousStep);
                this.updateDocumentCount();
            });
            containerImageDiv.appendChild(btn);
            li.appendChild(containerImageDiv);

            ul.appendChild(li);
        });

        this.identityDocumentsListsContainer.appendChild(listDiv);
    }

    /**
 * Create and display a tooltip
 */
    private showTooltip(event: MouseEvent, text: string) {
        let tooltip = document.createElement("div");
        tooltip.classList.add("custom-tooltip");
        tooltip.innerHTML = text;

        this.modalElement.appendChild(tooltip);

        // Ensure event.target is an HTMLElement
        const target = event.target as HTMLElement;
        if (!target) return;

        // Get the target's bounding box
        const rect = target.getBoundingClientRect();

        // Set tooltip position to the right of the element
        //tooltip.style.left = `${rect.right + window.scrollX + 10}px`; // 10px space from element
        //tooltip.style.top = `${rect.top + window.scrollY + rect.height / 2 - tooltip.offsetHeight / 2}px`;

        // Align horizontally to top-right center
        tooltip.style.left = `${rect.right + window.scrollX - tooltip.offsetWidth / 2}px`; 
        tooltip.style.top = `${rect.top + window.scrollY - tooltip.offsetHeight - 10}px`; 

    }
    /**
     * Remove the tooltip when mouse leaves
     */
    private hideTooltip() {
        const tooltip = document.querySelector(".custom-tooltip");
        if (tooltip) {
            tooltip.remove();
        }
    }

    private updateDocumentCount() {
        const documentsDiv = this.documentListItems.querySelector(".document-list-item");
        const selectedDocs = documentsDiv.querySelectorAll(".selected").length;
        const subheading = this.documentListItems.querySelector(".document-list-count");
        if (selectedDocs === this.totalDocumentListItems) {
            subheading.classList.add("all-selected");
            this.documentListButton.classList.remove("disabled")
        }
        else {
            this.documentListButton.classList.add("disabled")
        }
        subheading.textContent = `${selectedDocs}/${this.totalDocumentListItems} documents selected`;

    }

    /**
     * Handle the user selecting a particular document for a category
     */
    private onSelectDocument(categoryId: number, docId: number, docName: string, container: HTMLDivElement) {
        const docSelectId = container.getAttribute("data-documentselect-id")!;
        this.documentState.addDocument({ docSelectId, categoryId, selectedId: docId });

        // Update the .document-select-dropdown text for that category
        if (container) {
            const dropdownDiv = container.querySelector(".document-select-dropdown");

            if (dropdownDiv) {
                const dropdownNameDiv = dropdownDiv.querySelector(".document-name");
                dropdownNameDiv.innerHTML = `<div class="document-name-text">${docName}</div>`;
                const event = new Event("change", { bubbles: true });
                dropdownDiv.dispatchEvent(event);

                if (container == this.nameChangeDropdownContainer) {
                    container.classList.add("selected")
                    return;
                }
            }

            const containers: NodeListOf<Element> = container.closest(".document-list-item").querySelectorAll(".document-select-container");
            const docs = this.documentState.getAllDocuments();
            const duplicateIds = this.checkDuplicates(docs)
            containers.forEach(currentDocDiv => {
                const docSelectId = currentDocDiv.getAttribute("data-documentselect-id");
                const errorDiv = currentDocDiv.querySelector(".document-select-error") as HTMLDivElement;
                const chosenDoc = docs.find(d => d.docSelectId === docSelectId);

                const isEmpty = !chosenDoc || !chosenDoc.selectedId;

                if (chosenDoc && duplicateIds.includes(chosenDoc.selectedId)) {
                    errorDiv.textContent = "Document selected twice";
                    currentDocDiv.classList.remove("selected");
                    currentDocDiv.classList.add("error");
                } else {
                    currentDocDiv.classList.remove("error");
                    errorDiv.textContent = "";

                    if (!isEmpty) {
                        currentDocDiv.classList.add("selected");
                    } else {
                        currentDocDiv.classList.remove("selected");
                    }
                }
            });

        }
    }

    /**
     * Confirm selection => validate => go to contact details
     */
    private onConfirmSelectionClick() {
        const docSelection = this.documentListItems?.querySelector(
            `.document-list-item`
        ) as HTMLDivElement;
        if (this.validateDropdowns(docSelection)) {
            this.showStep("finalcheck");
        }
    }

    /**
     * Validate the user’s document selections
     */
    private validateDropdowns(document: HTMLDivElement): boolean {
        let isValid = true;
        if (!document) return false;

        // Clear errors
        const containers = document.querySelectorAll(".document-select-container");
        containers.forEach(c => {
            const errorDiv = c.querySelector(".document-select-error") as HTMLDivElement;
            c.classList.remove("error");
            errorDiv.textContent = "Please select a document";
        });

        // Check for missing selections
        containers.forEach(container => {
            const docSelectId = container.getAttribute("data-documentselect-id");
            const errorDiv = container.querySelector(".document-select-error") as HTMLDivElement;

            // Find the doc by docSelectId, not just categoryId
            const chosenDoc = this.documentState
                .getAllDocuments()
                .find(d => d.docSelectId === docSelectId);

            if (!chosenDoc) {
                isValid = false;
                container.classList.add("error");
                errorDiv.textContent = "Please select a document";
            }
        });

        const docs = this.documentState.getAllDocuments();
        const duplicateIds = this.checkDuplicates(docs)
        if (duplicateIds.length > 0) {
            isValid = false;
            containers.forEach(container => {
                const docSelectId = container.getAttribute("data-documentselect-id");
                const errorDiv = container.querySelector(".document-select-error") as HTMLDivElement;
                const chosenDoc = docs.find(d => d.docSelectId === docSelectId);

                if (chosenDoc && duplicateIds.includes(chosenDoc.selectedId)) {
                    container.classList.add("error");
                    errorDiv.textContent =
                        "Document selected twice";
                    if (container.classList.contains("selected")) container.classList.remove("selected");
                }
            });
        }

        return isValid;
    }

    private checkDuplicates(docs: IdentityDocument[]) {
        // Check for duplicates
        const docIds = docs.map(d => d.selectedId);

        const freq: Record<number, number> = {};
        docIds.forEach(id => (freq[id] = (freq[id] || 0) + 1));

        const duplicateIds = Object.keys(freq)
            .filter(idStr => freq[+idStr] > 1)
            .map(idStr => parseInt(idStr, 10));

        return duplicateIds

    }

    // Step navigation
    private showStep(step: string) {
        this.previousStep = this.currentStep;
        this.currentStep = step;
        let scrollTarget: HTMLElement | null = null;

        // Hide everything
        if (this.initialQuestionSection?.classList.contains("active")) this.initialQuestionSection?.classList.remove("active");
        if (this.combinationSection?.classList.contains("active")) this.combinationSection?.classList.remove("active");
        if (this.documentListSection?.classList.contains("active")) this.documentListSection?.classList.remove("active");
        if (this.identityDocumentSelectionSection?.classList.contains("active")) this.identityDocumentSelectionSection?.classList.remove("active");
        if (this.finalCheckSection?.classList.contains("active")) this.finalCheckSection?.classList.remove("active");
        if (this.checklistReviewSection?.classList.contains("active")) this.checklistReviewSection?.classList.remove("active");
        if (this.contactDetailsSection?.classList.contains("active")) this.contactDetailsSection?.classList.remove("active");
        if (this.checklistSentSection?.classList.contains("active")) this.checklistSentSection?.classList.remove("active")

        switch (step) {
            case "initialquestion":
                this.initialQuestionSection?.classList.add("active");
                this.disableBackButton(true);
                scrollTarget = this.initialQuestionSection;
                this.resetCombinations()
                break;
            case "combination":
                this.combinationSection?.classList.add("active");
                this.disableBackButton(false);
                scrollTarget = this.combinationSection;
                this.resetCombinations()
                break;
            case "documents":
                this.documentListSection?.classList.add("active");
                this.disableBackButton(false);
                scrollTarget = this.documentListSection;
                break;
            case "identity":
                this.identityDocumentSelectionSection?.classList.add("active");
                this.disableBackButton(false);
                scrollTarget = this.identityDocumentSelectionSection?.querySelector(".identity-document-selection-container") ?? null;
                break;
            case "finalcheck":
                this.finalCheckSection?.classList.add("active");;
                this.disableBackButton(false);
                this.attachFinalCheckListeners();
                scrollTarget = this.finalCheckSection;
                break;
            case "checklistreview":
                this.checklistReviewSection?.classList.add("active");;
                this.disableBackButton(false);
                scrollTarget = this.checklistReviewSection;
                break;
            case "contact":
                this.contactDetailsSection?.classList.add("active");;
                this.disableBackButton(false);
                scrollTarget = this.contactDetailsSection;
                break;
            case "sent":
                this.checklistSentSection?.classList.add("active");
                this.disableBackButton(false);
                scrollTarget = this.checklistSentSection;
                break;
        }
        if (scrollTarget)
            scrollTarget.scrollTop = 0;
    }

    private onBackButtonClick() {
        switch (this.currentStep) {
            case "combination":
                this.showStep("initialquestion");
                break;
            case "documents":
                // Determine if user came from "combination" or "initialquestion"
                if (this.previousStep === "combination") {
                    this.showStep("combination"); // Back to combination
                } else {
                    this.showStep("initialquestion"); // Back to initial question
                }
                break;
            case "identity":
                if (this.previousStep === "documents") {
                    this.showStep("documents"); // Back to combination
                } else {
                    this.showStep("finalcheck"); // Back to initial question
                }
                break;
            case "finalcheck":
                this.showStep("documents");
                break;
            case "checklistreview":
                this.showStep("finalcheck");
                break;
            case "contact":
                this.showStep("checklistreview");
                break;
            case "sent":
                this.showStep("contact");
                break;
        }
    }

    private attachFinalCheckListeners() {
        const nextButton = this.finalCheckSection?.querySelector(".final-check-button .button") as HTMLButtonElement;
        const signatureAmendSelections = this.signatureWarning.querySelector(".amend-selections")
        const nameChangeAmendSelections = this.nameChangeWarning.querySelector(".amend-selections")
        const dropDown = this.nameChangeDropdownContainer.querySelector(".document-select-dropdown");

        const updateValidation = () => {
            let valid = true;

            const signatureYesChecked = this.signatureYes.checked;
            const signatureNoChecked = this.signatureNo.checked;
            const nameYesChecked = this.nameYes.checked;
            const nameNoChecked = this.nameNo.checked;

            const docSelected = this.validateDropdowns(this.nameChangeDropdownContainer);

            // --- Signature validation ---
            if (!signatureYesChecked && !signatureNoChecked) {
                this.signatureWarning.style.display = "none";
                valid = false;
            } else if (signatureNoChecked) {
                this.signatureWarning.style.display = "flex";
                valid = false;
            } else {
                this.signatureWarning.style.display = "none";
            }

            // --- Name Change Dropdown Display ---
            if (nameYesChecked || nameNoChecked) {
                if (nameNoChecked) {
                    this.nameChangeDropdownContainer.style.display = "block";
                    this.nameChangeWarning.style.display = "flex";
                } else {
                    this.nameChangeDropdownContainer.style.display = "none";
                    this.nameChangeWarning.style.display = "none";
                }
            } else {
                valid = false;
            }

            // --- Core Logic Based on Signature + Name + Doc ---
            if (signatureNoChecked) {
                valid = false;
            } else if (signatureYesChecked) {
                if (nameYesChecked) {
                    valid = true;
                } else if (nameNoChecked && docSelected) {
                    valid = true;
                } else {
                    valid = false;
                }
            }
            // --- Enable/disable the "Next" button ---
            if (valid) {
                nextButton.classList.remove("disabled");
                nextButton.disabled = false;
            } else {
                nextButton.classList.add("disabled");
                nextButton.disabled = true;
            }
        };


        // Attach radio change handlers
        [this.signatureYes, this.signatureNo, this.nameYes, this.nameNo].forEach(radio => {
            radio.addEventListener("change", updateValidation);
        });

        dropDown.addEventListener("change", updateValidation)

        signatureAmendSelections.addEventListener("click", () => {
            this.showStep("documents")
        })
        nameChangeAmendSelections.addEventListener("click", () => {
            this.showStep("documents")
        })        

        // 4) If everything is valid, proceed to checklist
        nextButton.addEventListener("click", () => {

            if (!nextButton.disabled) {
                this.renderChecklistReviewSection();
                this.showStep("checklistreview");
            }

        });

    }

    private renderChecklistReviewSection() {
        const checklistContainer = this.modalElement.querySelector(".checklist-final-checklist") as HTMLUListElement;
        if (!checklistContainer) return;
        const editDiv = this.checklistReviewSection.querySelector(".heading-div-edit");

        checklistContainer.innerHTML = "";

        if (!this.signatureYes || !this.signatureNo || !this.nameYes || !this.nameNo) return;

        const allDocs = this.documentState.getAllDocuments();

        const checklistItems = allDocs
            .map(d => {
                const category = this.categoryTypes.find(ct => ct.itemID === d.categoryId);
                const document = this.documents.find(doc => doc.itemID === d.selectedId);
                if (category && document) {
                    return {
                        text: `${category.name}: ${document.name}`,
                        isNameChange: d.categoryId === 7
                    };
                }
                return null;
            })
            .filter(item => item !== null)
            .sort((a, b) => {
                if (a!.isNameChange && !b!.isNameChange) return 1;
                if (!a!.isNameChange && b!.isNameChange) return -1;
                return a!.text.localeCompare(b!.text);
            });

        checklistItems.forEach(item => {
            const li = document.createElement("li");
            li.textContent = item!.text;
            checklistContainer.appendChild(li);
        });

        editDiv.addEventListener("click", () => {
            this.showStep("documents");
        });
        this.attachChecklistReviewListeners();
    }

    private attachChecklistReviewListeners() {
        const closeButton = this.modalElement.querySelector(".close-window-button .button") as HTMLButtonElement;
        const emailButton = this.modalElement.querySelector(".email-me-list-button .button") as HTMLButtonElement;

        if (closeButton) {
            closeButton.addEventListener("click", () => {
                const modalInstance = Modal.getInstance(this.modalElement);
                modalInstance?.hide();
                this.resetModal();
            });
        }

        if (emailButton) {
            emailButton.addEventListener("click", () => {
                this.showStep("contact")
            });
        }
    }

    private validateEmail(): boolean {
        if (!this.emailInput || !this.emailErrorMessage) return false;
        const val = this.emailInput.textContent.trim();

        if (!this.emailRegex.test(val)) {
            this.emailInput.classList.add("highlight-error");
            this.emailErrorMessage.style.display = "block";
            return false;
        }

        this.emailInput.classList.remove("highlight-error");
        this.emailErrorMessage.style.display = "none";
        return true;
    }

    private async sendIdentityDocuments() {
        if (!this.validateEmail()) return;
        const sentDescriptionText = this.checklistSentSection.querySelector(".description-text")

        const submission: IdentityDocumentsSubmission = {
            email: this.emailInput!.textContent.trim(),
            identityDocuments: this.documentState.getAllDocuments(),
        };

        try {
            const resp = await fetch("/api/proofofidentity/sendidentitydocuments", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(submission),
            });
            if (resp.status === 204) {
                sentDescriptionText.innerHTML = `An email has been sent to <strong>${this.emailInput!.textContent.trim()}</strong> and should arrive within the next few minutes.`
                this.showStep("sent");
            } else {
                console.warn("Unexpected response:", await resp.json());
                //Need to show errors here 
            }
        } catch (err) {
            console.error("Error sending identity docs:", err);
            //Need to show errors here
        }
    }

    private resetModal() {
        this.resetCombinations();
        this.showStep("initialquestion");

    }

    private resetCombinations() {
        this.documentState.reset();
        this.currentCombinationId = null;
        this.totalDocumentListItems = 0;

        // Hide all identity type lists
        if (this.identityDocumentsListsContainer) {
            this.identityDocumentsListsContainer.innerHTML = "";
        }

        // Clear doc selections if needed
        this.documentListItems && (this.documentListItems.innerHTML = "");

        //Clear final checks checklsit 
        this.nameChangeDropdownContainer.style.display = "none";
        this.nameChangeWarning.style.display = "none";
        this.signatureWarning.style.display = "none";
        this.signatureYes.checked = false;
        this.signatureNo.checked = false;
        this.nameYes.checked = false;
        this.nameNo.checked = false;
        // Reset the dropdown text
        const dropdownNameDiv = this.nameChangeDropdownContainer.querySelector(".document-name");
        if (dropdownNameDiv) {
            dropdownNameDiv.innerHTML = `<div class="document-name-text">Select a document</div> 
                                         <i class="dot-icon dot-stroke-forward-arrow"></i>`;
        }

        // Find the nearest parent with .name-change-dropdown and remove the "selected" class
        const parentDropdown = this.nameChangeDropdownContainer.closest(".name-change-dropdown");
        if (parentDropdown) {
            parentDropdown.classList.remove("selected");
        }

        // Reset email
        if (this.emailInput) {
            this.emailInput.textContent = "";
            this.emailInput.classList.remove("highlight-error");
        }
        if (this.emailErrorMessage) this.emailErrorMessage.style.display = "none";

    }

    private disableBackButton(disabled: boolean) {
        if (!this.backButton) return;
        if (disabled) {
            this.backButton.classList.add("disabled");
            this.backButton.setAttribute("disabled", "true");
        } else {
            this.backButton.classList.remove("disabled");
            this.backButton.removeAttribute("disabled");
        }
    }
}
